import { getProjectUrl } from 'base/blocks_routes';
import { exists, UnreachableError } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { ProductRestrictions } from 'base/product_restrictions';
import { Button } from 'base/ui/button';
import { renderIcon } from 'base/ui/colored_icon';
import { Dialog } from 'base/ui/dialog';
import { RedErrorText, useSubmit } from 'base/ui/form';
import { IconGlobe, IconGroup, IconLink } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { LoadingDots } from 'base/ui/loading_dots';
import { DropdownIcon, MenuButton } from 'base/ui/menu';
import { menuItem } from 'base/ui/menu_model';
import { DividerLine } from 'base/ui/panel';
import { ToastQueue } from 'base/ui/toast';
import { TextRegular, TextRegularLight, TextSmall } from 'base/ui/typography';
import { useStableCallback } from 'base/useStableCallback';
import {
  AccessLevel,
  BlocksService,
  ProjectUpdateSharingSettingsRequest,
  SharingSettings,
} from 'blocks/blocks_service';
import { ProjectSession } from 'blocks/editor/session/session';
import { ShowUpgradeWallFn } from 'blocks/editor/session/upgrade_dialog';
import { getExtraProjectShareData } from 'blocks/editor/styleguide/helpers';
import { manageAccountRouteToUrl } from 'blocks/manage_account/routes';
import React from 'react';
import styles from './sharing_settings.css';

export function SharingSettingsDialog({
  session,
  library,
  blocksService,
  toastQueue,
  onClose,
}: {
  session: ProjectSession;
  library: LibraryCache;
  blocksService: BlocksService;
  toastQueue: ToastQueue;
  onClose: () => void;
}) {
  const [settings, setSettings] = React.useState<SharingSettings | undefined>();
  const [initialError, setInitialError] = React.useState<unknown>();
  const { projectToken, productRestrictions, showUpgradeWall } = session;

  React.useEffect(() => {
    blocksService
      .projectGetSharingSettings(projectToken)
      .then(setSettings)
      .catch(setInitialError);
  }, [blocksService, projectToken]);

  const [loading, submitError, changeSettings] = useSubmit(
    async (req: Omit<ProjectUpdateSharingSettingsRequest, 'projectToken'>) => {
      const res = await blocksService.projectUpdateSharingSettings({
        ...req,
        projectToken,
      });
      setSettings(res);
    },
  );
  const rowProps: Omit<RowProps, 'settings'> = {
    changeSettings,
    loading,
    productRestrictions,
    showUpgradeWall,
  };

  const onClickCopy = useStableCallback(async () => {
    const link = getProjectUrl(projectToken);
    navigator.clipboard.writeText(link);
    toastQueue.push({ label: 'Copied to clipboard' });

    session.analytics.track({
      event: 'project:copy_share_link_button_click',
      share_link_access_level: settings?.linkAccessLevel,
      extra: await getExtraProjectShareData(session.project.state, library),
    });
  });

  return (
    <Dialog
      title="Share project"
      onClose={onClose}
      buttons={
        <Button
          icon={IconLink}
          label="Copy link"
          onClick={onClickCopy}
          variant="primary"
        />
      }
    >
      <Rows spacing={8}>
        {settings ? (
          <Rows spacing={16}>
            <TeamSharingRow {...rowProps} settings={settings} />
            <DividerLine />
            <LinkSharingRow {...rowProps} settings={settings} />
            <RedErrorText error={submitError} />
          </Rows>
        ) : initialError ? (
          <RedErrorText error={initialError} />
        ) : (
          <LoadingDots aspectRatio={4 / 1} />
        )}
        <div />
      </Rows>
    </Dialog>
  );
}

type RowProps = {
  settings: SharingSettings;
  changeSettings: (
    req: Omit<ProjectUpdateSharingSettingsRequest, 'projectToken'>,
  ) => Promise<void>;
  loading: boolean;
  productRestrictions: ProductRestrictions;
  showUpgradeWall?: ShowUpgradeWallFn;
};

function TeamSharingRow({
  settings,
  changeSettings,
  loading,
  productRestrictions,
}: RowProps) {
  if (!productRestrictions.teamEnabled) {
    return (
      <ButtonRow icon={IconGroup} text="Create a team to invite editors">
        <Button
          href={manageAccountRouteToUrl({
            path: 'manage-account/team',
          })}
          label="Invite"
        />
      </ButtonRow>
    );
  }
  const teamName = (
    <TextRegular tag="span">{settings.teamName || 'your team'}</TextRegular>
  );
  if (settings.ownershipType === 'user') {
    return (
      <ButtonRow
        icon={IconGroup}
        text={
          <>
            {'Move project to share with '}
            {teamName}
          </>
        }
      >
        <Button
          label="Move"
          onClick={() => changeSettings({ ownershipType: 'team' })}
          loading={loading}
          disabled={!settings.canUpdateSharingSettings}
          tooltip={
            settings.canUpdateSharingSettings
              ? undefined
              : 'You do not have permission to change sharing settings'
          }
        />
      </ButtonRow>
    );
  } else if (settings.ownershipType === 'team') {
    return (
      <ButtonRow
        icon={IconGroup}
        text={
          <>
            {'Anyone in '}
            {teamName}
            {' can edit'}
          </>
        }
      >
        <div />
      </ButtonRow>
    );
  } else {
    throw new UnreachableError(settings.ownershipType);
  }
}

function LinkSharingRow({
  settings,
  changeSettings,
  loading,
  productRestrictions,
  showUpgradeWall,
}: RowProps) {
  const commentDisabled =
    productRestrictions.maximumPublicAccessLevel < AccessLevel.COMMENTER;
  const menu = [
    menuItem({
      label: 'No access',
      checked: settings.linkAccessLevel === AccessLevel.NONE,
      onClick: () => changeSettings({ linkAccessLevel: AccessLevel.NONE }),
    }),
    menuItem({
      label: 'Can view',
      checked: settings.linkAccessLevel === AccessLevel.VIEWER,
      onClick: () => changeSettings({ linkAccessLevel: AccessLevel.VIEWER }),
    }),
    menuItem({
      label: 'Can comment',
      checked: settings.linkAccessLevel === AccessLevel.COMMENTER,
      onClick: commentDisabled
        ? () => showUpgradeWall?.('share')
        : () => changeSettings({ linkAccessLevel: AccessLevel.COMMENTER }),
      subtlyDisabled: commentDisabled,
      componentRight: commentDisabled && (
        <TextSmall color="gradient">Upgrade</TextSmall>
      ),
    }),
    settings.linkAccessLevel === AccessLevel.OWNER
      ? menuItem({
          label: 'Can edit',
          checked: true,
          disabled: true,
        })
      : undefined,
  ].filter(exists);
  const selectedLabel = menu.find((item) => item.checked)?.label || 'Unknown';

  return (
    <ButtonRow icon={IconGlobe} text="Anyone with the link">
      <MenuButton
        label={selectedLabel}
        loading={loading}
        menu={menu}
        search="hidden"
        xalign="left"
        iconRight={DropdownIcon}
        disabled={!settings.canUpdateSharingSettings}
        tooltip={
          settings.canUpdateSharingSettings
            ? undefined
            : 'You do not have permission to change sharing settings'
        }
      />
    </ButtonRow>
  );
}

function ButtonRow({
  icon,
  text,
  children,
}: {
  icon: React.ReactNode | React.ComponentType;
  text: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.buttonRow}>
      <div className={styles.buttonRowIcon}>{renderIcon(icon)}</div>
      <TextRegularLight>{text}</TextRegularLight>
      {children}
    </div>
  );
}
